import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';
import { UsersService } from '../../../shared/services/users.service';
import { AcknowledementConstants } from '../../acknowledement-constants';
import { Acknowledgement } from '../../models/acknowledement';
import { AcknowledgementInteractionService } from '../../services/acknowledgement-interaction.service';
import { AcknowledgementService } from '../../services/acknowledgement.service';

@Component({
  selector: 'app-acknowledgement-creation',
  templateUrl: './acknowledgement-creation.component.html',
  styleUrls: ['./acknowledgement-creation.component.scss'],
})
export class AcknowledgementCreationComponent implements OnInit {
  @Input() documentNumber!: string;
  @Input() documentVersion!: string;
  @Input() documentEffectiveDate?: Date;
  @Input() documentPhaseNumber!: number;

  acknowledgement: Acknowledgement = new Acknowledgement();
  isHardcopyAcknowledgementSaveInProgress = false;
  hardcopyAcknowledgementSaveProgressMessage =
    AcknowledementConstants.Acknowledgement_Save_InProgress;
  errorMessages: string[] = [];
  errorMessageHeaderText: string =
    AcknowledementConstants.Acknowledgement_Error_Message_Header_Text;
  acknowledgementForm!: FormGroup;
  mandatoryFields: { [key: string]: boolean } =
    AcknowledementConstants.Acknowledgement_Default_Requiredfields;
  minDueDateValue: Date = new Date();
  userList: any = [];
  valueField: string = 'value';
  labelField: string = 'name';
  dueDateLabel = 'Due Date';
  isDueDateActive = true;

  constructor(
    private _formBuilder: FormBuilder,
    private usersService: UsersService,
    private acknowledgementInteractionService: AcknowledgementInteractionService,
    private changeDetectorRef: ChangeDetectorRef,
    private hardCopyActivityService: AcknowledgementService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.documentNumber;
    this.documentEffectiveDate;
    this.initialize();
    this.acknowledgementForm = this.createAcknowledgementForm();

    //In future when we eliminate reloading the page on phase move then we may get issue interms of input chnage event.
    //To capture that we need to move this code to common method and need to call this in ngOnchanges as well.
    if (this.documentPhaseNumber != AcknowledementConstants.documentEffectivePhasenumber) {
      this.dueDateLabel;
      this.acknowledgementForm.controls['dueDate'].disable();
    } else {
      this.dueDateLabel = this.dueDateLabel + '*';
      this.acknowledgementForm.controls['dueDate'].enable();
    }
  }

  createAcknowledgementForm() {
    return this._formBuilder.group({
      assignedInternalUser: new FormControl(null),
      dueDate: new FormControl(null, [Validators.required]),
    });
  }

  initialize() {
    //To reset the form
    this.acknowledgementInteractionService.closeAndResetAcknowledgementSlider$.subscribe(
      (isClose) => {
        if (isClose) {
          this.closeAndResetAcknowledgementSlider();
        }
      }
    );

    // To load users data
    this.usersService.getUsers().subscribe((usersData) => {
      for (let dataItem of usersData) {
        const value = dataItem.name;
        this.userList.push({
          value,
          name: value,
        });
      }
    });
  }

  closeAndResetAcknowledgementSlider() {
    this.acknowledgementForm.reset();
    this.errorMessages = [];
    Object.keys(this.acknowledgementForm.controls).forEach((key) => {
      this.mandatoryFields[key] = false;
    });
    this.isDueDateActive = false;
    // Using Angular change detection
    this.changeDetectorRef.detectChanges();
    this.isDueDateActive = true;
  }

  saveHardcopyAcknowledgement() {
    this.validateForm();
    if (!this.errorMessages.length) {
      this.saveAcknowledgement();
    }
  }

  validateForm() {
    this.errorMessages = [];
    this.mandatoryFields =
      AcknowledementConstants.Acknowledgement_Default_Requiredfields;
    if (this.acknowledgementForm.valid) {
      return;
    }
    Object.keys(this.acknowledgementForm.controls).forEach((key) => {
      if (
        this.acknowledgementForm.get(key)?.errors &&
        this.documentPhaseNumber ==
          AcknowledementConstants.documentEffectivePhasenumber
      ) {
        this.errorMessages.push(this.getErrorMessageText(key));
      } else {
        this.mandatoryFields[key] = false;
      }
    });
  }

  private getErrorMessageText(labelName: string): string {
    const errorLabelMapper: { [key: string]: string } = {
      dueDate: AcknowledementConstants.Acknowledgement_DueDate_Required,
    };
    this.mandatoryFields[labelName] = true;
    return errorLabelMapper[labelName];
  }

  saveAcknowledgement() {
    this.getAcknowledgementDetails();
    this.isHardcopyAcknowledgementSaveInProgress = true;

    this.hardCopyActivityService
      .saveAcknowledgementDetails(this.acknowledgement)
      .pipe(
        catchError((err) => {
          this.messageService.add({
            severity: AcknowledementConstants.Severity_Err,
            summary: `${AcknowledementConstants.Acknowledgement_Creation_Failed}`,
          });
          this.isHardcopyAcknowledgementSaveInProgress = false;
          return throwError(() => err);
        })
      )
      .subscribe((resp) => {
        this.messageService.add({
          severity: AcknowledementConstants.Severity_Success,
          summary: `${AcknowledementConstants.Acknowledgement_Created_Success}`,
        });
        this.isHardcopyAcknowledgementSaveInProgress = false;
        this.acknowledgementInteractionService.closeAndResetAcknowledgementSlider$.next(true);
        this.acknowledgementInteractionService.acknowledgementConfirmationCompleted.next(true);
      });
  }

  getAcknowledgementDetails() {
    if (
      this.documentPhaseNumber ===
      AcknowledementConstants.documentEffectivePhasenumber
    )
      this.acknowledgement.dueDate =
        this.acknowledgementForm.get('dueDate')?.value;
    else if (
      this.documentPhaseNumber === AcknowledementConstants.documentApprovedPhasenumber
    ) {
      this.acknowledgement.dueDate = this.documentEffectiveDate;
    }
    AcknowledementConstants.documentPhasenumbersToHideAcknowledgementBtn2.includes(
      this.documentPhaseNumber
    )
      ? (this.acknowledgement.status = '2')
      : (this.acknowledgement.status = '1');

    this.acknowledgement.assignedInternalUser =
      this.acknowledgementForm.get('assignedInternalUser')?.value;

    this.acknowledgement.parentDocumentNumber = this.documentNumber;
    this.acknowledgement.parentDocumentVersion = +this.documentVersion;
  }
}
